import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { useDarkMode } from "../../components/context/darkModeContext";
import { planForecastRunService } from "../../utils/services/planForecastRunService";
import { catalogPlanService } from "../../utils/services/catalogPlanService";
import { catalogPlanManagementService } from "../../utils/services/catalogPlanManagementService";
import { buildForecastTree, gridLines } from "./forecastTree";
import ForecastGrid from "./ForecastGrid";
import { ProductCard } from "./ProductCard";
import { downloadBlocksXlsx } from "./forecastXlsx";

const STALE_TEXT = {
  new_actuals: "a month has closed since the run (new actuals)",
  inventory_changed: "stock on hand has changed",
  promo_changed: "promo days or forecast settings have changed",
};

const yearGap = (node, closed) => gridLines(node, closed, "_units").year.delta;
const yearGapMoney = (node, closed) => gridLines(node, closed, "").year.delta;

/** A status chip with an ⓘ that opens the full message (e.g. which SKUs failed and why). */
function InfoChip({ label, color, title, lines = [] }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Chip
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{ fontSize: 11 }}
        deleteIcon={
          <Tooltip title="Details">
            <InfoOutlinedIcon />
          </Tooltip>
        }
        onDelete={(e) => setAnchor(e.currentTarget.closest(".MuiChip-root") || e.currentTarget)}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, maxWidth: 520, maxHeight: 360, overflowY: "auto" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: lines.length ? 1 : 0 }}>{title}.</Typography>
          {lines.map((l) => (
            <Typography key={l} sx={{ fontSize: 12, py: 0.25, wordBreak: "break-word" }}>
              {l}
            </Typography>
          ))}
        </Box>
      </Popover>
    </>
  );
}

/** Loading placeholder shaped like the page: header, chips, category grid, product cards. */
function ForecastPageSkeleton({ darkMode }) {
  const paper = { p: 2, mb: 2, bgcolor: darkMode ? "#1a1a1a" : "#fff" };
  return (
    <Box sx={{ py: 2.5, px: 0, bgcolor: darkMode ? "#121212" : "#f4f6f8", minHeight: "100vh" }} aria-busy="true">
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Skeleton variant="circular" width={26} height={26} />
            <Skeleton variant="text" width={320} height={30} />
          </Box>
          <Skeleton variant="text" width={420} height={18} sx={{ ml: 4 }} />
        </Box>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={140} height={32} />
          <Skeleton variant="rounded" width={120} height={32} />
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
        {[110, 120, 100, 95, 120].map((w, i) => (
          <Skeleton key={i} variant="rounded" width={w} height={24} sx={{ borderRadius: 3 }} />
        ))}
      </Box>
      <Paper sx={paper}>
        <Skeleton variant="text" width={140} height={24} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={28} sx={{ mb: 0.5 }} />
        {Array.from({ length: 10 }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={22} sx={{ mb: 0.5, opacity: 0.7 }} />
        ))}
      </Paper>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1 }}>
        <Skeleton variant="text" width={130} height={26} sx={{ mr: "auto" }} />
        <Skeleton variant="rounded" width={240} height={36} />
        <Skeleton variant="rounded" width={200} height={36} />
      </Box>
      {Array.from({ length: 6 }, (_, i) => (
        <Paper key={i} sx={{ ...paper, mb: 0.75, display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" height={22} />
            <Skeleton variant="text" width="12%" height={16} />
          </Box>
          {[80, 80, 80, 80].map((w, j) => (
            <Skeleton key={j} variant="text" width={w} height={36} />
          ))}
          <Skeleton variant="rounded" width={170} height={28} />
        </Paper>
      ))}
    </Box>
  );
}

/**
 * Planning → Demand Plan → View Forecast: the latest forecast run of a plan
 * category against the plan, rolled up SKU → Product → Category, in units and $.
 */
// Buttons, fields and focus rings take the theme's primary colour, which is
// the client's brand colour (often red). Red is kept for problems on this
// page, so in light mode its primary is the planner's blue.
const plannerBlue = (outer) =>
  outer.palette.mode === "dark"
    ? outer
    : { ...outer, palette: { ...outer.palette, primary: outer.palette.augmentColor({ color: { main: "#1976d2" }, name: "primary" }) } };

export default function CatalogPlanForecast() {
  return (
    <ThemeProvider theme={plannerBlue}>
      <ForecastPage />
    </ThemeProvider>
  );
}

function ForecastPage() {
  const { id: planId, runId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useDarkMode();
  const [run, setRun] = useState(null);
  const [plan, setPlan] = useState(null);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("gap");
  const [openProduct, setOpenProduct] = useState({});
  // Stable callbacks for the memoised product cards.
  const toggleProduct = useCallback(
    (id, open) => setOpenProduct((prev) => ({ ...prev, [id]: open })),
    [],
  );
  const downloadProductRef = useRef(null);
  const downloadProduct = useCallback((p) => downloadProductRef.current?.(p), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      planForecastRunService.get(runId),
      catalogPlanService.getById(planId),
      catalogPlanManagementService.skuShares(planId).catch(() => ({ shares: [] })),
    ])
      .then(([r, p, s]) => {
        if (cancelled) return;
        setRun(r);
        setPlan(p);
        setShares(s?.shares || []);
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.message || "The forecast could not be loaded."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [planId, runId]);

  // Opened by URL: put the category name in the route state for the breadcrumb.
  useEffect(() => {
    if (run?.row_name && !location.state?.categoryName) {
      navigate(location.pathname, { replace: true, state: { ...(location.state || {}), categoryName: run.row_name } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.row_name]);

  const tree = useMemo(
    () => (run && plan ? buildForecastTree({ run, plan, shares }) : null),
    [run, plan, shares],
  );

  const textSec = darkMode ? "#aaa" : "#666";
  // Sort key for products and SKUs (ascending): most behind plan first, or
  // highest forecast first.
  const sortKey = useMemo(() => {
    if (!tree) return () => 0;
    if (sortBy === "gap") return (n) => yearGap(n, tree.closed) ?? 0;
    if (sortBy === "gap$") return (n) => yearGapMoney(n, tree.closed) ?? 0;
    return (n) => -(gridLines(n, tree.closed, "_units").year.forecast || 0);
  }, [tree, sortBy]);
  const sorted = (list) => [...list].sort((a, b) => sortKey(a) - sortKey(b));

  const products = useMemo(() => {
    if (!tree) return [];
    const q = search.trim().toLowerCase();
    const list = tree.products
      .map((p) => ({
        ...p,
        skus: q && !p.name.toLowerCase().includes(q) ? p.skus.filter((s) => s.sku.toLowerCase().includes(q)) : p.skus,
      }))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.skus.length);
    return sorted(list).map((p) => ({ ...p, skus: sorted(p.skus) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, search, sortKey]);

  // Every product (search ignored), in the page's sort order — for the download.
  const allProducts = useMemo(
    () => (tree ? sorted(tree.products).map((p) => ({ ...p, skus: sorted(p.skus) })) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tree, sortKey],
  );

  if (loading) return <ForecastPageSkeleton darkMode={darkMode} />;
  if (error || !tree) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "The forecast could not be loaded."}</Alert>
      </Box>
    );
  }

  const params = run.params || {};
  const paramChips = [
    `Lead time ${params.lead_time_days ?? "—"} d`,
    `Safety stock ${params.safety_stock_percentage ?? "—"}%`,
    params.include_lost_sales ? "Lost sales on" : "Lost sales off",
    params.sales_fill ? "Sales fill on (new SKUs estimated)" : null,
    params.discount ? `Promo ${params.discount}%` : null,
    params.warehouse?.length ? `${params.warehouse.length} warehouse(s)` : "All warehouses",
    params.channel?.length ? `Channels: ${params.channel.join(", ")}` : null,
    params.history_start ? `History from ${params.history_start}` : null,
  ].filter(Boolean);
  const paperSx = { p: 2, mb: 2, bgcolor: darkMode ? "#1a1a1a" : "#fff" };

  // ── Excel: one block per product ──
  const aboutRows = (scope) => [
    ["Category", run.row_name || ""],
    ["Plan", plan.name || ""],
    ["Plan year", run.year],
    ["Forecast run", new Date(run.started_at).toLocaleString()],
    ["Run by", run.triggered_by?.email || ""],
    ["SKUs in run", run.sku_count],
    ["In this file", scope],
    ["Parameters", paramChips.join(" · ")],
    ["Note", "Closed months show actuals; open months the forecast. Product and SKU $ are units × list price."],
  ];
  const downloadProducts = () =>
    downloadBlocksXlsx(allProducts, tree.closed, {
      sheetName: "Products",
      fileName: `${run.row_name || "category"}-forecast-${run.year}-products`,
      info: aboutRows(`All ${allProducts.length} products`),
    });
  // One product: its SKUs, one block each.
  downloadProductRef.current = (p) =>
    downloadBlocksXlsx(p.skus, tree.closed, {
      sheetName: "SKUs",
      fileName: `${p.name}-forecast-${run.year}-skus`,
      info: aboutRows(`${p.name}: ${p.skus.length} SKUs`),
    });

  // ── Expand / collapse ───────────────────────────────────────────────────
  const allProductsOpen = products.length > 0 && products.every((p) => openProduct[p.id]);
  const toggleAllProducts = () =>
    setOpenProduct(allProductsOpen ? {} : Object.fromEntries(products.map((p) => [p.id, true])));

  return (
    <Box sx={{ py: 2.5, px: 0, bgcolor: darkMode ? "#121212" : "#f4f6f8", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
        <Box sx={{ textAlign: "left" }}>
          {/* Back arrow on the title line; the details line sits under the title. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={() => navigate(`/catalog-planner/${planId}`)}
              size="small"
              sx={{ ml: -0.75 }}
              aria-label="Back to the plan"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3, textAlign: "left" }}>
              {run.row_name || "Category"} · forecast vs plan {run.year}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: textSec, textAlign: "left", pl: 4 }}>
            {plan.name} · run {new Date(run.started_at).toLocaleString()}
            {run.triggered_by?.email ? ` by ${run.triggered_by.email}` : ""} · {run.sku_count} SKUs
          </Typography>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", gap: 1, pt: 0.25 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={downloadProducts}
            disabled={!allProducts.length}
            sx={{ textTransform: "none" }}
          >
            Download Excel
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={allProductsOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            onClick={toggleAllProducts}
            disabled={!products.length}
            sx={{ textTransform: "none" }}
          >
            {allProductsOpen ? "Collapse All" : "Expand All"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
        {paramChips.map((c) => (
          <Chip key={c} label={c} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        ))}
        {run.email?.status === "sent" && (
          <Chip label={`Excel emailed to ${run.email.to}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        )}
        {run.email?.status === "error" && (
          <InfoChip
            label="Excel email failed"
            color="error"
            title={`The Excel couldn't be emailed to ${run.email.to || "you"}: ${run.email.reason || "unknown error"}`}
          />
        )}
        {run.stale && (
          <InfoChip
            label="Stale"
            color="warning"
            title={`This forecast may be out of date: ${(run.stale_reasons || [])
              .map((r) => STALE_TEXT[r] || r)
              .join("; ")}. The gap still uses your latest plan; run the forecast again from the plan when you want fresh numbers`}
          />
        )}
        {run.failed_count > 0 && (
          <InfoChip
            label={`${run.failed_count} of ${run.sku_count} SKUs failed`}
            color="error"
            title={`${run.failed_count} of ${run.sku_count} SKUs couldn't be forecast and are left out`}
            lines={(run.failures || []).map((f) => `${f.sku}: ${f.message}`)}
          />
        )}
        {run.result?.missing_price_skus?.length > 0 && (
          <InfoChip
            label={`${run.result.missing_price_skus.length} SKUs without list price`}
            color="warning"
            title="These SKUs have no list price, so their forecast counts in units but not in $"
            lines={run.result.missing_price_skus}
          />
        )}
      </Box>

      <Paper sx={paperSx}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>{run.row_name || "Category"}</Typography>
        <ForecastGrid node={tree.category} closed={tree.closed} darkMode={darkMode} />
        <Typography sx={{ fontSize: 11, color: textSec, mt: 1, fontStyle: "italic" }}>
          Closed months show actuals · open months the forecast (italic) · gap = forecast − plan, so editing the plan
          updates it without a new run. Product and SKU $ are units × list price.
        </Typography>
      </Paper>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mr: "auto" }}>
          Products ({products.length})
        </Typography>
        <TextField
          size="small"
          placeholder="Search product or SKU"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240 }}
        />
        <Select size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value)} sx={{ fontSize: 13 }}>
          <MenuItem value="gap">Most behind plan (units)</MenuItem>
          <MenuItem value="gap$">Most behind plan ($)</MenuItem>
          <MenuItem value="forecast">Highest forecast</MenuItem>
        </Select>
      </Box>

      {products.map((p) => (
        <ProductCard
          key={p.id}
          product={p}
          closed={tree.closed}
          darkMode={darkMode}
          textSec={textSec}
          open={!!openProduct[p.id]}
          onToggle={toggleProduct}
          onDownload={downloadProduct}
        />
      ))}
    </Box>
  );
}
